import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { Button, IconButton } from '@/components/ui/button';

type Lang = 'cs' | 'en';

type Section = { title: string; paragraphs: string[] };

const CONTENT: Record<Lang, { heading: string; intro: string; effective: string; sections: Section[] }> = {
  cs: {
    heading: 'Podmínky používání',
    effective: 'Účinné od 1. 1. 2026',
    intro:
      'Tyto podmínky upravují používání softwaru pro plánování ortodontické léčby a přípravu výroby zdravotnických prostředků na zakázku, který provozuje Avaros Life s.r.o., IČO 094 36 481, se sídlem Tomešova 562/2a, Staré Brno, 602 00 Brno (dále jen „Poskytovatel“).',
    sections: [
      {
        title: '1. Komu je software určen',
        paragraphs: [
          'Software je profesionální nástroj určený výhradně oprávněným odborníkům — ortodontistům a zubním klinikám. Není určen k použití pacienty ani laickou veřejností.',
          'Přístup je vázán na uživatelský účet. Uživatel odpovídá za zabezpečení přihlašovacích údajů a za veškerou činnost provedenou pod jeho účtem.',
        ],
      },
      {
        title: '2. Odborná odpovědnost',
        paragraphs: [
          'Software je pomůckou pro vizualizaci a přípravu léčby. Veškerá klinická rozhodnutí, posouzení vhodnosti léčby a schválení plánu léčby jsou plně v odpovědnosti ošetřujícího ortodontisty.',
          'Poskytovatel neposkytuje lékařské poradenství a nenahrazuje odborný úsudek zdravotnického pracovníka.',
        ],
      },
      {
        title: '3. Zpracování dat a soukromí',
        paragraphs: [
          'Data pacientů a skenů jsou zpracovávána výhradně za účelem poskytování služby (plánování léčby a příprava výroby).',
          'Poskytovatel nepředává data pacientů třetím stranám. Data nejsou prodávána ani využívána k marketingu.',
          'Správcem osobních údajů pacientů zůstává klinika, resp. ošetřující ortodontista, který odpovídá za získání souhlasu pacienta. Poskytovatel vystupuje jako zpracovatel a data zpracovává podle pokynů správce.',
        ],
      },
      {
        title: '4. Přípustné užití',
        paragraphs: [
          'Uživatel se zavazuje používat software v souladu s právními předpisy a pouze k určenému účelu.',
          'Není dovoleno software zpětně analyzovat, narušovat jeho provoz, pokoušet se o neoprávněný přístup ani jej využívat k poškození třetích osob.',
        ],
      },
      {
        title: '5. Duševní vlastnictví',
        paragraphs: [
          'Software, jeho zdrojový kód a vizuální podoba jsou duševním vlastnictvím Poskytovatele a jsou chráněny příslušnými právními předpisy. Uživateli je poskytováno nepřevoditelné právo k užívání po dobu platnosti přístupu.',
        ],
      },
      {
        title: '6. Dostupnost a odpovědnost',
        paragraphs: [
          'Software je poskytován „tak, jak je“. Poskytovatel vyvíjí přiměřené úsilí o jeho dostupnost a správnou funkci, nezaručuje však nepřerušovaný ani bezchybný provoz.',
          'Poskytovatel neodpovídá za škody vzniklé v důsledku klinických rozhodnutí učiněných na základě výstupů softwaru.',
        ],
      },
      {
        title: '7. Změny podmínek',
        paragraphs: [
          'Poskytovatel může tyto podmínky přiměřeně aktualizovat. O podstatných změnách bude uživatele informovat. Pokračováním v používání softwaru po účinnosti změn vyjadřuje uživatel souhlas s novým zněním.',
        ],
      },
      {
        title: '8. Rozhodné právo a kontakt',
        paragraphs: [
          'Tyto podmínky se řídí právním řádem České republiky.',
          'Kontakt: info@avaros.cz, www.avaros.cz.',
        ],
      },
    ],
  },
  en: {
    heading: 'Terms of Usage',
    effective: 'Effective from 1 January 2026',
    intro:
      'These terms govern the use of the software for orthodontic treatment planning and the preparation of custom-made medical devices, operated by Avaros Life s.r.o., Company ID 094 36 481, registered at Tomešova 562/2a, Staré Brno, 602 00 Brno, Czech Republic (the “Provider”).',
    sections: [
      {
        title: '1. Intended users',
        paragraphs: [
          'The software is a professional tool intended solely for authorized professionals — orthodontists and dental clinics. It is not intended for use by patients or the general public.',
          'Access is tied to a user account. The user is responsible for keeping their credentials secure and for all activity carried out under their account.',
        ],
      },
      {
        title: '2. Professional responsibility',
        paragraphs: [
          'The software is an aid for visualizing and preparing treatment. All clinical decisions, assessments of treatment suitability, and approval of the treatment plan remain fully the responsibility of the treating orthodontist.',
          'The Provider does not give medical advice and does not replace the professional judgment of a healthcare practitioner.',
        ],
      },
      {
        title: '3. Data processing and privacy',
        paragraphs: [
          'Patient and scan data are processed solely to provide the service (treatment planning and manufacturing preparation).',
          'The Provider does not share patient data with third parties. Data is neither sold nor used for marketing.',
          'The clinic, i.e. the treating orthodontist, remains the controller of patients’ personal data and is responsible for obtaining patient consent. The Provider acts as a processor and processes data according to the controller’s instructions.',
        ],
      },
      {
        title: '4. Acceptable use',
        paragraphs: [
          'The user agrees to use the software in accordance with the law and only for its intended purpose.',
          'Reverse engineering, disrupting operation, attempting unauthorized access, or using the software to harm third parties is not permitted.',
        ],
      },
      {
        title: '5. Intellectual property',
        paragraphs: [
          'The software, its source code, and its visual design are the intellectual property of the Provider and are protected by applicable law. The user is granted a non-transferable right of use for the duration of their access.',
        ],
      },
      {
        title: '6. Availability and liability',
        paragraphs: [
          'The software is provided “as is”. The Provider makes reasonable efforts to keep it available and functioning correctly but does not guarantee uninterrupted or error-free operation.',
          'The Provider is not liable for damages resulting from clinical decisions made on the basis of the software’s output.',
        ],
      },
      {
        title: '7. Changes to the terms',
        paragraphs: [
          'The Provider may reasonably update these terms and will inform users of material changes. Continued use of the software after the changes take effect constitutes acceptance of the new version.',
        ],
      },
      {
        title: '8. Governing law and contact',
        paragraphs: [
          'These terms are governed by the laws of the Czech Republic.',
          'Contact: info@avaros.cz, www.avaros.cz.',
        ],
      },
    ],
  },
};

export default function TermsOfUsage() {
  useDocumentTitle('Settings — Terms of Usage');
  const navigate = useNavigate();
  const [lang, setLang] = useState<Lang>('cs');
  const content = CONTENT[lang];

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-auto p-1">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <IconButton
            variant="ghost-light"
            size="icon"
            icon={ArrowLeft}
            aria-label="Back"
            onClick={() => navigate(-1)}
          />
          <div>
            <h1 className="text-lg font-semibold text-white/90">{content.heading}</h1>
            <p className="text-xs text-white/45">{content.effective}</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            size="sm"
            variant={lang === 'cs' ? 'default' : 'outline-white'}
            onClick={() => setLang('cs')}
          >
            CZ
          </Button>
          <Button
            type="button"
            size="sm"
            variant={lang === 'en' ? 'default' : 'outline-white'}
            onClick={() => setLang('en')}
          >
            EN
          </Button>
        </div>
      </div>

      <section className="surface-panel max-w-3xl space-y-5 p-6 text-white/80">
        <p className="text-sm leading-relaxed text-white/70">{content.intro}</p>

        {content.sections.map((section) => (
          <div key={section.title} className="space-y-1.5">
            <h2 className="text-sm font-semibold text-white/90">{section.title}</h2>
            {section.paragraphs.map((paragraph, index) => (
              <p key={index} className="text-sm leading-relaxed text-white/65">
                {paragraph}
              </p>
            ))}
          </div>
        ))}
      </section>
    </div>
  );
}
